import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import client from '../contentful';

const ProjectTemplate = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    client.getEntries({ 
      content_type: 'project'
    })
    .then(response => {
      // Find the project by matching the entry ID or slug
      const project = response.items.find(item => item.sys.id === id || item.fields.slug === id);
      
      if (!project) {
        setError('Project not found');
      } else {
        setPost(project);
      }
    })
    .catch(err => {
      console.error('Error fetching project:', err);
      setError('Failed to load project');
    })
    .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-white pt-24 text-center text-gray-500">
      Loading...
    </div>
  );
  
  if (error) return (
    <div className="min-h-screen bg-white pt-24 text-center text-red-500">
      {error}
    </div>
  );
  
  if (!post) return (
    <div className="min-h-screen bg-white pt-24 text-center text-gray-500">
      Project not found
    </div>
  );

  const { client: projectClient, industry, description, projectSummary, description1, description2, description3, description4, description5, caption5, title, tags } = post.fields;

  // Debug: Log all available fields
  console.log('=== PROJECT TEMPLATE DEBUG ===');
  console.log('All available fields:', Object.keys(post.fields));
  console.log('Post fields data:', post.fields);
  console.log('projectClient:', projectClient);
  console.log('title:', title);

  // Helper function to render rich text with formatting
  const renderRichText = (field, textSize = "text-base md:text-lg") => {
    if (!field) return null;
    
    // If it's a string, return it as paragraph
    if (typeof field === 'string') {
      return <p className={`${textSize} text-gray-700 leading-relaxed mb-4`}>{field}</p>;
    }
    
    // If it's a rich text object, render with proper formatting
    if (field.content && Array.isArray(field.content)) {
      return (
        <>
          {field.content.map((block, blockIdx) => {
            // Handle paragraph blocks
            if (block.nodeType === 'paragraph') {
              return (
                <p key={blockIdx} className={`${textSize} text-gray-700 leading-relaxed mb-4`}>
                  {block.content?.map((item, itemIdx) => (
                    <span key={itemIdx}>
                      {item.value}
                      {item.marks?.includes('bold') && <strong>{item.value}</strong>}
                      {item.marks?.includes('italic') && <em>{item.value}</em>}
                    </span>
                  ))}
                </p>
              );
            }
            
            // Handle unordered lists
            if (block.nodeType === 'unordered-list') {
              return (
                <ul key={blockIdx} className={`list-disc list-inside ${textSize} text-gray-700 leading-relaxed mb-4 ml-4`}>
                  {block.content?.map((item, itemIdx) => (
                    <li key={itemIdx} className="mb-2">
                      {item.content?.[0]?.content?.map((textItem, textIdx) => (
                        <span key={textIdx}>{textItem.value}</span>
                      ))}
                    </li>
                  ))}
                </ul>
              );
            }
            
            // Handle ordered lists
            if (block.nodeType === 'ordered-list') {
              return (
                <ol key={blockIdx} className={`list-decimal list-inside ${textSize} text-gray-700 leading-relaxed mb-4 ml-4`}>
                  {block.content?.map((item, itemIdx) => (
                    <li key={itemIdx} className="mb-2">
                      {item.content?.[0]?.content?.map((textItem, textIdx) => (
                        <span key={textIdx}>{textItem.value}</span>
                      ))}
                    </li>
                  ))}
                </ol>
              );
            }
            
            // Handle headings
            if (block.nodeType?.includes('heading')) {
              const level = block.nodeType.match(/\d+/)?.[0] || '3';
              const headingClass = level === '1' ? 'text-2xl' : level === '2' ? 'text-xl' : 'text-lg';
              return (
                <h3 key={blockIdx} className={`${headingClass} font-bold text-dj-dark mb-4`}>
                  {block.content?.map((item, itemIdx) => (
                    <span key={itemIdx}>{item.value}</span>
                  ))}
                </h3>
              );
            }
            
            return null;
          })}
        </>
      );
    }
    
    return null;
  };

  // Collect all image fields
  const imageFields = Object.values(post.fields).filter(
    value => value && value.fields && value.fields.file && value.fields.file.url
  );

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Map field names to custom display labels
  const fieldLabelMap = {
    projectSummary: 'ABOUT THE PROJECT'
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-dj-dark py-4 px-4 md:px-20 pt-6">
        <a href="/#case-studies" className="inline-flex items-center gap-2 text-primary hover:text-secondary transition-colors">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Work</span>
        </a>
        
      </div>

      {/* Hero Section */}
      {imageFields.length > 0 && (
        <div className="w-full h-64 md:h-96 bg-center bg-cover">
          <img 
            src={imageFields[0].fields.file.url} 
            alt={title || projectClient} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      {/* Header Section */}
      <div className="bg-dj-dark text-white py-16 md:py-28 px-4 md:px-20">
        <div className="max-w-6xl mx-auto">
          {(projectClient || industry) && (
            <h1 className="text-lg md:text-2xl font-semibold mb-4">
              {projectClient && <span className="text-primary font-bold">{projectClient}</span>}
              {projectClient && industry && <span className="text-secondary mx-2">|</span>}
              {industry && <span className="text-white font-medium">{industry}</span>}
            </h1>
          )}
          {title && <h1 className="text-5xl md:text-8xl font-bold mb-8 leading-tight text-white">{title}</h1>}
          {tags && Array.isArray(tags) && tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <span key={tag} className="bg-primary/20 text-primary px-3 py-1.5 rounded-full text-xs font-semibold">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="py-12 md:py-20 px-4 md:px-20 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Two-Column Layout: Content + Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 md:gap-16 mb-16">
            {/* Left Column: Main Content */}
            <div className="lg:col-span-2">
              {/* Overview */}
              {description && (
                <div className="mb-12">
                  <div className="text-4xl md:text-6xl font-medium text-gray-700 leading-relaxed">
                    {renderRichText(description, "text-2xl md:text-4xl")}
                  </div>
                </div>
              )}

              {/* Project Summary */}
              {projectSummary && (
                <div className="mb-12">
                   <h2 className="text-sm font-semibold text-secondary uppercase tracking-widest mb-6">OVERVIEW</h2>
                  {renderRichText(projectSummary)}
                </div>
              )}

              {/* Image 1 - After Overview */}
              {imageFields.length > 1 && (
                <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={imageFields[1].fields.file.url}
                    alt={title || projectClient}
                    className="w-full h-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}

              {/* Description 1 */}
              {description1 && (
                <div className="mb-12">
 
                  {renderRichText(description1)}
                </div>
              )}

              {/* Image 2 - After Description 1 */}
              {imageFields.length > 2 && imageFields[2] ? (
                <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={imageFields[2].fields.file.url}
                    alt={title || projectClient}
                    className="w-full h-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ) : (
                imageFields.length <= 2 && description1 && (
                  <div className="mb-12 p-4 bg-gray-100 rounded-lg text-gray-600 text-sm">
                    <p>Note: Add a second image to Contentful to display here</p>
                  </div>
                )
              )}

              {/* Challenge */}
              {description2 && (
                <div className="mb-12">

                  {renderRichText(description2)}
                </div>
              )}

              {/* Image 3 - After Challenge */}
              {imageFields.length > 3 && (
                <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={imageFields[3].fields.file.url}
                    alt={title || projectClient}
                    className="w-full h-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}

              {/* Solution */}
              {description3 && (
                <div className="mb-12">

                  {renderRichText(description3)}
                </div>
              )}

              {/* Results */}
              {description4 && (
                <div className="mb-12">

                  {renderRichText(description4)}
                </div>
              )}

              {/* Image 4 - After Description 4 */}
              {imageFields.length > 4 && (
                <div className="mb-12">
                  <div className="overflow-hidden rounded-lg shadow-lg mb-4">
                    <img 
                      src={imageFields[4].fields.file.url}
                      alt={title || projectClient}
                      className="w-full h-auto hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  {caption5 && (
                    <p className="text-sm text-gray-600 italic">{caption5}</p>
                  )}
                </div>
              )}

              {/* Description 5 */}
              {description5 && (
                <div className="mb-12">
                  {renderRichText(description5)}
                </div>
              )}

              {/* Image 5 - After Description 5 */}
              {imageFields.length > 5 && (
                <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
                  <img 
                    src={imageFields[5].fields.file.url}
                    alt={title || projectClient}
                    className="w-full h-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
            </div>

            {/* Right Column: Key Details */}
            <div className="lg:col-span-1">
              <div className="bg-dj-light rounded-lg p-6 md:p-8 sticky top-24 h-fit">
                    <h3 className="text-sm font-semibold text-secondary uppercase tracking-widest mb-6">Project Details</h3>
                
                {/* Client */}
                {projectClient && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2">Client</p>
                    <p className="text-gray-800 font-medium">{projectClient}</p>
                  </div>
                )}

                {/* Industry */}
                {industry && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-2">Industry</p>
                    <p className="text-gray-800 font-medium">{industry}</p>
                  </div>
                )}

                {/* Other Details */}
                {Object.entries(post.fields).filter(([key]) => 
                  !["client","industry","description","projectSummary","description1","description2","description3","description4","description5","caption5","tags","title","slug","heroImage"].includes(key)
                ).some(([, value]) => value && !(value?.fields?.file?.url)) && (
                  <>
                    <div className="border-t border-gray-300 pt-6">
                      {Object.entries(post.fields).map(([key, value]) => {
                        if (["client","industry","description","projectSummary","description1","description2","description3","description4","description5","caption5","tags","title","slug"].includes(key)) return null;
                        if (value && value.fields && value.fields.file && value.fields.file.url) return null;
                        if (!value) return null;

                        return (
                          <div key={key} className="mb-4">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest mb-1">
                              {fieldLabelMap[key] || key.replace(/([A-Z])/g, ' $1').trim()}
                            </p>
                            <p className="text-gray-800 text-sm">
                              {renderValue(value)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Additional Gallery - Remaining Images */}
          {imageFields.length > 6 && (
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-dj-dark mb-8">More Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {imageFields.slice(6).map((img, idx) => (
                  <div key={img.fields.file.url + idx} className="overflow-hidden rounded-lg shadow-lg">
                    <img
                      src={img.fields.file.url}
                      alt={title || projectClient}
                      className="w-full h-auto hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 pt-12 border-t border-gray-200 text-center">
            <h3 className="text-2xl font-bold text-dj-dark mb-4">Ready to start your project?</h3>
            <p className="text-gray-600 mb-6">Let's discuss how I can help bring your vision to life.</p>
            <a href="#contact" className="inline-block bg-primary hover:bg-secondary text-dj-dark font-bold py-3 px-8 rounded transition-colors">
              Start a Project
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTemplate;
